import { useEffect, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import FilmCard from "@/components/base/FilmCard";
import usePageMeta from "@/hooks/usePageMeta";
import { hotKeywords, searchFilms } from "@/lib/films";

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q")?.trim() ?? "";
  const [input, setInput] = useState(keyword);
  const results = searchFilms(keyword);

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  usePageMeta({
    title: keyword ? `${keyword} 搜索结果 - 人兽影视网` : "站内搜索 - 人兽影视网",
    description: keyword
      ? `人兽影视网关于「${keyword}」的搜索结果，包含相关电影、电视剧的剧情介绍、演员信息与影评分析。`
      : "在人兽影视网搜索电影、电视剧、演员或类型关键词，快速找到想看的影视作品与影评解读。",
    keywords: keyword
      ? `${keyword},搜索结果,影视搜索,剧情介绍,影评`
      : "影视搜索,站内搜索,电影搜索,电视剧搜索,人兽影视网",
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = input.trim();
    if (!value) return;
    setSearchParams({ q: value });
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="container-portal pt-6 md:pt-8">
        <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
          <Link to="/" className="transition-colors hover:text-primary-600 cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-900">站内搜索</span>
        </nav>

        <header className="mt-5 border-b border-background-200 pb-6">
          <span className="label-caps text-[11px] text-accent-700">Search</span>
          <h1 className="mt-2 text-3xl font-black tracking-tight text-foreground-950 md:text-[36px]">
            {keyword ? `「${keyword}」的搜索结果` : "站内搜索"}
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            {keyword
              ? `共找到 ${results.length} 部相关影视作品，搜索范围包含片名、导演、演员、类型与标签。`
              : "输入片名、演员或类型关键词，例如「暗河」「悬疑」「长街旧梦」。"}
          </p>

          <form onSubmit={handleSubmit} className="mt-5 flex flex-col gap-2 sm:flex-row">
            <div className="flex h-11 flex-1 items-center rounded-md border border-background-300 bg-background-50 pl-3.5 pr-1 focus-within:border-primary-400 transition-colors">
              <i className="ri-search-line text-base text-foreground-500" />
              <input
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="搜索影视作品、演员、类型"
                className="h-full flex-1 bg-transparent px-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none"
                aria-label="搜索关键词"
              />
            </div>
            <button
              type="submit"
              className="inline-flex h-11 items-center justify-center gap-1.5 rounded-md bg-primary-500 px-5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-search-line" />
              搜索
            </button>
          </form>

          <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-foreground-500">
            <span>热门关键词</span>
            {hotKeywords.map((word) => (
              <button
                key={word}
                type="button"
                onClick={() => setSearchParams({ q: word })}
                className="rounded-full border border-background-300 bg-background-100 px-2.5 py-1 text-[11px] text-foreground-700 transition-colors hover:border-accent-300 hover:text-accent-700 cursor-pointer whitespace-nowrap"
              >
                {word}
              </button>
            ))}
          </div>
        </header>

        <div className="mt-7 pb-4">
          {keyword && results.length > 0 && (
            <div className="grid grid-cols-2 gap-x-3 gap-y-7 sm:grid-cols-3 sm:gap-x-4 lg:grid-cols-5">
              {results.map((film) => (
                <FilmCard key={film.id} film={film} showType />
              ))}
            </div>
          )}

          {keyword && results.length === 0 && (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-search-eye-line text-2xl" />
              </span>
              <h2 className="mt-4 text-base font-semibold text-foreground-900">
                没有找到与「{keyword}」相关的影视作品
              </h2>
              <p className="mt-2 max-w-sm text-xs leading-6 text-foreground-500">
                试试换成片名、演员名字或更宽泛的类型词，例如「悬疑」「家庭」「2026」。
              </p>
              <div className="mt-5 flex flex-wrap justify-center gap-2">
                {hotKeywords.slice(0, 4).map((word) => (
                  <button
                    key={word}
                    type="button"
                    onClick={() => setSearchParams({ q: word })}
                    className="rounded-md bg-background-100 px-3 py-2 text-xs text-foreground-700 transition-colors hover:bg-background-200 cursor-pointer whitespace-nowrap"
                  >
                    {word}
                  </button>
                ))}
              </div>
            </div>
          )}

          {!keyword && (
            <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
              <h2 className="text-base font-bold text-foreground-950">你可以这样开始</h2>
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                {[
                  { icon: "ri-movie-2-line", title: "按片名搜索", text: "输入完整或部分片名，如「暗河」。" },
                  { icon: "ri-user-star-line", title: "按演员搜索", text: "输入主演姓名，找到其相关作品。" },
                  { icon: "ri-price-tag-3-line", title: "按类型搜索", text: "输入「悬疑」「年代」「治愈」等标签。" },
                ].map((item) => (
                  <div key={item.title} className="rounded-md border border-background-200 bg-background-50 p-4">
                    <i className={`${item.icon} text-lg text-accent-600`} />
                    <h3 className="mt-2 text-sm font-semibold text-foreground-950">{item.title}</h3>
                    <p className="mt-1 text-xs leading-5 text-foreground-600">{item.text}</p>
                  </div>
                ))}
              </div>
              <p className="mt-5 text-[11px] leading-5 text-foreground-500">
                本站搜索为前端模拟检索，结果来自站内示例数据，不连接外部数据库或第三方接口。
              </p>
            </div>
          )}

          <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-600 cursor-pointer">
              <i className="ri-home-4-line mr-1" />
              返回首页
            </Link>
            <Link to="/category/movie" className="transition-colors hover:text-primary-600 cursor-pointer">
              <i className="ri-movie-2-line mr-1" />
              浏览电影
            </Link>
            <Link to="/category/tv" className="transition-colors hover:text-primary-600 cursor-pointer">
              <i className="ri-tv-2-line mr-1" />
              浏览电视剧
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}